import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface OverflowMenuItem {
  key: string;
  /** Material symbol name. */
  icon?: IconName;
  label: string;
  onClick?: () => void;
  /** `danger` renders the item in red. */
  variant?: 'default' | 'danger';
  disabled?: boolean;
  /** Native tooltip — useful to explain why a `disabled` item is blocked. */
  title?: string;
  /** Static submenu items. Mutually exclusive with `loadSubmenu`. */
  submenu?: OverflowMenuItem[];
  /** Async loader for submenu items. Called once when the submenu first opens. */
  loadSubmenu?: () => Promise<OverflowMenuItem[]>;
  /** Replaces the placeholder shown while a `loadSubmenu` resolves. Defaults to "cargando…". */
  loadingLabel?: string;
  /** Replaces the placeholder shown when a submenu resolves to no items. Defaults to "sin resultados". */
  emptyLabel?: string;
  /** Optional content rendered on the right side of the row (e.g. count badge). */
  trailing?: React.ReactNode;
  /** Optional automation hook rendered as `data-testid` on the item button. */
  testId?: string;
}

interface Props {
  items: OverflowMenuItem[];
  /** Accessible label for the trigger button. */
  ariaLabel?: string;
  /** Icon rendered in the default trigger. Defaults to `more_vert`. */
  triggerIcon?: IconName;
  /**
   * Replace the default `⋮` trigger. Receives the open state so the trigger can
   * reflect it (e.g. rotate a `+` into an `×`). The menu still owns the ref,
   * the click handling, the portal and the positioning — pass presentation only.
   *
   * Exists so `CreateActions` can show a round `+` without a second portalled
   * menu implementation living alongside this one.
   */
  renderTrigger?: (state: { open: boolean }) => React.ReactNode;
  /** Optional extra className for the trigger button. */
  className?: string;
  /** Optional automation hook rendered as `data-testid` on the trigger button. */
  triggerTestId?: string;
}

interface SubmenuState {
  items: OverflowMenuItem[] | null;
  loading: boolean;
  error: string | null;
}

const MENU_WIDTH = 224; // ~14rem — must match `min-w-[14rem]` below

/**
 * Three-dot overflow menu with anchored dropdown.
 *
 * The dropdown is rendered through a portal so it escapes any `overflow-hidden`
 * container higher up the tree. Items can declare a `submenu` (static) or
 * `loadSubmenu` (async); hovering them opens a sub-flyout to the left.
 *
 * ```tsx
 * <OverflowMenu items={[
 *   { key: 'reload', icon: 'refresh', label: 'recargar', onClick: reload },
 *   { key: 'designs', icon: 'palette', label: 'diseños', loadSubmenu: fetchDesigns },
 *   { key: 'delete', icon: 'delete', label: 'eliminar', variant: 'danger', onClick: del },
 * ]} />
 * ```
 */
export default function OverflowMenu({
  items,
  ariaLabel = 'más acciones',
  triggerIcon = 'more_vert',
  renderTrigger,
  className = '',
  triggerTestId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [openSubmenuKey, setOpenSubmenuKey] = useState<string | null>(null);
  const [submenuCache, setSubmenuCache] = useState<Record<string, SubmenuState>>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Right-aligned, with vertical flip so a menu on the last row of a list opens
  // upward instead of getting clipped at the bottom of the viewport.
  const pos = useAnchoredPosition(triggerRef, open, {
    align: 'right',
    estimatedWidth: MENU_WIDTH,
    estimatedHeight: Math.min(items.length, 8) * 44 + 16,
    gap: 4,
  });
  const submenuSide: 'right' | 'left' =
    pos && pos.left + MENU_WIDTH * 2 + 8 < window.innerWidth ? 'right' : 'left';

  // Keyboard: focus lands on the first item when the menu opens; arrows move
  // between enabled items (inside the open submenu when there is one), Home /
  // End jump, ArrowRight / Enter open a submenu, ArrowLeft closes it, Escape
  // closes and returns focus to the trigger.
  const focusableItems = (): HTMLButtonElement[] => {
    const root = menuRef.current;
    if (!root) return [];
    const scope = openSubmenuKey
      ? (root.querySelector<HTMLElement>(`[data-submenu-of="${CSS.escape(openSubmenuKey)}"]`) ?? root)
      : root;
    return Array.from(scope.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])'))
      .filter((el) => scope === root ? !el.closest('[data-submenu-of]') : true);
  };
  const moveFocus = (delta: number | 'first' | 'last') => {
    const items = focusableItems();
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    let next = delta === 'first' ? 0 : delta === 'last' ? items.length - 1 : current + delta;
    if (next < 0) next = items.length - 1;
    if (next >= items.length) next = 0;
    items[next]?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          if (openSubmenuKey) setOpenSubmenuKey(null);
          else { setOpen(false); triggerRef.current?.focus(); }
          break;
        case 'ArrowDown': e.preventDefault(); moveFocus(1); break;
        case 'ArrowUp': e.preventDefault(); moveFocus(-1); break;
        case 'Home': e.preventDefault(); moveFocus('first'); break;
        case 'End': e.preventDefault(); moveFocus('last'); break;
        case 'ArrowLeft':
          if (openSubmenuKey) {
            e.preventDefault();
            const key = openSubmenuKey;
            setOpenSubmenuKey(null);
            menuRef.current?.querySelector<HTMLButtonElement>(`[data-item-key="${CSS.escape(key)}"]`)?.focus();
          }
          break;
        case 'ArrowRight': {
          const active = document.activeElement as HTMLElement | null;
          const key = active?.getAttribute('data-item-key');
          const item = key ? items.find((i) => i.key === key) : undefined;
          if (item && (item.submenu || item.loadSubmenu) && !item.disabled) {
            e.preventDefault();
            handleSubmenuOpen(item);
          }
          break;
        }
        case 'Tab':
          setOpen(false);
          setOpenSubmenuKey(null);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openSubmenuKey, items]);

  // Focus the first item once the menu is positioned (opened by keyboard or
  // mouse alike — a mouse user who then presses ArrowDown expects it too).
  useEffect(() => {
    if (!open || !pos) return;
    const t = requestAnimationFrame(() => moveFocus('first'));
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pos !== null]);
  // When a submenu opens, move into it.
  useEffect(() => {
    if (!open || !openSubmenuKey) return;
    const t = requestAnimationFrame(() => moveFocus('first'));
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSubmenuKey, submenuCache[openSubmenuKey ?? '']?.items]);

  const closeAll = () => {
    setOpen(false);
    setOpenSubmenuKey(null);
  };

  const handleSubmenuOpen = (item: OverflowMenuItem) => {
    setOpenSubmenuKey(item.key);
    if (item.submenu) {
      const submenuItems = item.submenu;
      setSubmenuCache((c) =>
        c[item.key]
          ? c
          : { ...c, [item.key]: { items: submenuItems, loading: false, error: null } },
      );
      return;
    }
    if (item.loadSubmenu && !submenuCache[item.key]) {
      setSubmenuCache((c) => ({ ...c, [item.key]: { items: null, loading: true, error: null } }));
      item
        .loadSubmenu()
        .then((resolved) => {
          setSubmenuCache((c) => ({
            ...c,
            [item.key]: { items: resolved, loading: false, error: null },
          }));
        })
        .catch((err) => {
          setSubmenuCache((c) => ({
            ...c,
            [item.key]: {
              items: null,
              loading: false,
              error: err instanceof Error ? err.message : 'error',
            },
          }));
        });
    }
  };

  if (items.length === 0) return null;

  const renderItem = (item: OverflowMenuItem, isSubmenu = false, index = 0, count = 1) => {
    const isDanger = item.variant === 'danger';
    const hasSubmenu = !!(item.submenu || item.loadSubmenu);
    // The menu is `overflow-visible` so its submenus can stick out, which
    // also lets a hovered item's square background poke past the rounded
    // corners: the first and last items take the inner radius themselves
    // (`rounded-xl` minus the 1px border).
    const cornerClass = `${index === 0 ? 'rounded-t-[11px]' : ''} ${index === count - 1 ? 'rounded-b-[11px]' : ''}`;
    const baseClass =
      `w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left ${cornerClass}`;
    const colorClass = isDanger
      ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800';

    return (
      <div
        key={item.key}
        className="relative"
        onMouseEnter={() => {
          if (!isSubmenu && hasSubmenu && !item.disabled) handleSubmenuOpen(item);
          else if (!isSubmenu) setOpenSubmenuKey(null);
        }}
      >
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          aria-haspopup={hasSubmenu ? 'menu' : undefined}
          aria-expanded={hasSubmenu ? openSubmenuKey === item.key : undefined}
          data-item-key={item.key}
          title={item.title}
          data-testid={item.testId}
          disabled={item.disabled}
          onClick={() => {
            if (hasSubmenu) {
              if (!item.disabled) handleSubmenuOpen(item);
              return;
            }
            closeAll();
            item.onClick?.();
          }}
          className={`${baseClass} ${colorClass}`}
        >
          {item.icon ? (
            <Icon name={item.icon} className="!text-[18px]" aria-hidden="true" />
          ) : null}
          <span className="flex-1 truncate">{item.label}</span>
          {item.trailing ? <span className="ml-2 shrink-0">{item.trailing}</span> : null}
          {hasSubmenu ? <Icon name="chevron_right" className="!text-[16px] text-slate-400" aria-hidden="true" /> : null}
        </button>

        {hasSubmenu && openSubmenuKey === item.key ? renderSubmenu(item) : null}
      </div>
    );
  };

  const renderSubmenu = (item: OverflowMenuItem) => {
    const state = submenuCache[item.key];
    const sideCls =
      submenuSide === 'right' ? 'left-full top-0 ml-1' : 'right-full top-0 mr-1';
    return (
      <div
        role="menu"
        data-submenu-of={item.key}
        aria-label={item.label}
        className={`absolute ${sideCls} z-[61] min-w-[14rem] max-w-[20rem] bg-white dark:bg-surface-dark border border-border-strong dark:border-border-strong-dark rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100`}
      >
        {state?.loading ? (
          <div className="px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400">
            {item.loadingLabel ?? 'cargando…'}
          </div>
        ) : state?.error ? (
          <div className="px-4 py-2.5 text-sm text-red-500">{state.error}</div>
        ) : state?.items && state.items.length > 0 ? (
          state.items.map((sub, i, all) => renderItem(sub, true, i, all.length))
        ) : (
          <div className="px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400">
            {item.emptyLabel ?? 'sin resultados'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={triggerTestId}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
          setOpenSubmenuKey(null);
        }}
        className={
          renderTrigger
            ? className
            : `inline-flex items-center justify-center min-w-10 min-h-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${className}`
        }
      >
        {renderTrigger ? (
          renderTrigger({ open })
        ) : (
          <Icon name={triggerIcon} className="!text-[22px] text-slate-500 dark:text-slate-400" aria-hidden="true" />
        )}
      </button>
      {open && pos
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[59]" onClick={closeAll} />
              <div
                ref={menuRef}
                role="menu"
                aria-label={ariaLabel}
                style={{ top: pos.top, bottom: pos.bottom, left: pos.left }}
                className={`fixed z-[60] min-w-[14rem] bg-white dark:bg-surface-dark border border-border-strong dark:border-border-strong-dark rounded-xl shadow-2xl overflow-visible animate-in fade-in zoom-in-95 duration-100 ${pos.flip ? 'origin-bottom' : 'origin-top'}`}
              >
                {items.map((item, i) => renderItem(item, false, i, items.length))}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
